// flameco-auth core (skill v3). Framework-free browser client for the Flameco
// Authentication Service: hosted sign-in (OAuth code + PKCE), token storage,
// can(tag), an authenticated fetch, and sign-out. No dependencies.
//
// Sign in once (v3): signIn() first asks the auth service silently
// (prompt=none, once per tab), so someone already signed in to another
// Flameco app lands here signed in without a click. On the auth service's own
// site (*.flameco.com, localhost) that is one background call that hands back
// the token: no trip through the login page, no second load of the app. While the app is open it
// re-checks the shared session when the tab regains focus or is clicked (at
// most once a minute): signed out everywhere, or someone else signed in on
// this browser, drops this app's token too; a token close to expiry is renewed
// silently. The re-check needs the auth service on the same site (*.flameco.com,
// or localhost in development); elsewhere (a LAN address) the token's own
// expiry decides.

export interface FlamecoUser {
  id: string;
  employeeId: string;
  name: string;
  email: string | null;
  role: 'Administrator' | 'Manager' | 'Advanced' | 'Transactor' | 'Reader';
  /** What this person may do in THIS app, as set in the auth console. */
  tags: string[];
  /** Token expiry, epoch seconds. */
  exp: number;
}

export interface FlamecoAuthOptions {
  /** Auth service URL, e.g. https://auth.flameco.com (no trailing slash). */
  authUrl: string;
  /** This app's client id (from flameco-auth.json). */
  clientId: string;
  /** Where the login page sends people back. Default: `<this origin>/auth/callback`. Must be registered. */
  redirectUri?: string;
  /** How to open the login page. Default: same tab. Capacitor apps pass Browser.open. */
  navigate?: (url: string) => void | Promise<void>;
  /** Called when an API answers 401 or the token expires. Default: signIn(). */
  onUnauthorized?: () => void;
  /** Try a silent sign-in (prompt=none) before showing the login page. Default: true. */
  silentSignIn?: boolean;
  /** Follow the shared Flameco session while the app is open (see above). Default: true. */
  watchSession?: boolean;
}

/** A silent sign-in found nobody signed in. `returnTo` is where the person was going. */
export class LoginRequiredError extends Error {
  returnTo: string;
  constructor(message: string, returnTo = '/') {
    super(message);
    this.returnTo = returnTo;
  }
}

// Options after configure(): the return URL is always known.
type ResolvedOptions = FlamecoAuthOptions & { redirectUri: string };
let opts: ResolvedOptions | null = null;
let token: string | null = null;
let user: FlamecoUser | null = null;
let expiryTimer: ReturnType<typeof setTimeout> | undefined;
// Set while signOut() is on its way to the auth service's /logout, so an
// automatic signIn() (a guard reacting to the sign-out) can't cancel it.
let leaving = false;
const listeners = new Set<(u: FlamecoUser | null) => void>();
const CALLBACK_PATH = '/auth/callback';
// Renew the token silently when less than this is left on it.
const RENEW_BEFORE_MS = 30 * 60_000;
const CHECK_EVERY_MS = 60_000;
let lastCheck = 0;

const storeKey = () => `flameco_auth:${need().clientId}`;
const flowKey = () => `flameco_auth_flow:${need().clientId}`;
// Set (per tab) once a silent sign-in has been tried, so a "nobody signed in"
// answer goes on to the login page instead of asking silently again.
const silentKey = () => `flameco_auth_silent:${need().clientId}`;

function need(): ResolvedOptions {
  if (!opts) throw new Error('flameco-auth: call configure() first');
  return opts;
}

function safe<T>(fn: () => T, fallback: T): T {
  try {
    return fn();
  } catch {
    return fallback;
  }
}

// SHA-256 for the PKCE challenge. Browsers only expose crypto.subtle on secure
// origins (https or localhost), so a dev page opened over the LAN as
// http://192.168.x.x:3060 has none and sign-in crashed. Use the native digest
// when it exists; otherwise this small FIPS 180-4 implementation (same output).
const K256 = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

export function sha256Fallback(data: Uint8Array): Uint8Array {
  const bitLen = data.length * 8;
  const padded = new Uint8Array(((data.length + 9 + 63) >> 6) << 6);
  padded.set(data);
  padded[data.length] = 0x80;
  const view = new DataView(padded.buffer);
  view.setUint32(padded.length - 8, Math.floor(bitLen / 0x100000000));
  view.setUint32(padded.length - 4, bitLen >>> 0);
  const h = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ]);
  const w = new Uint32Array(64);
  const rotr = (x: number, n: number) => (x >>> n) | (x << (32 - n));
  for (let off = 0; off < padded.length; off += 64) {
    for (let i = 0; i < 16; i++) w[i] = view.getUint32(off + i * 4);
    for (let i = 16; i < 64; i++) {
      const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3);
      const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10);
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0;
    }
    let a = h[0], b = h[1], c = h[2], d = h[3], e = h[4], f = h[5], g = h[6], hh = h[7];
    for (let i = 0; i < 64; i++) {
      const t1 = (hh + (rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25)) + ((e & f) ^ (~e & g)) + K256[i] + w[i]) >>> 0;
      const t2 = ((rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22)) + ((a & b) ^ (a & c) ^ (b & c))) >>> 0;
      hh = g; g = f; f = e; e = (d + t1) >>> 0; d = c; c = b; b = a; a = (t1 + t2) >>> 0;
    }
    h[0] += a; h[1] += b; h[2] += c; h[3] += d; h[4] += e; h[5] += f; h[6] += g; h[7] += hh;
  }
  const out = new Uint8Array(32);
  const outView = new DataView(out.buffer);
  h.forEach((v, i) => outView.setUint32(i * 4, v));
  return out;
}

async function sha256(data: Uint8Array): Promise<ArrayBuffer | Uint8Array> {
  const subtle = typeof crypto !== 'undefined' ? crypto.subtle : undefined;
  return subtle ? subtle.digest('SHA-256', data as BufferSource) : sha256Fallback(data);
}

function b64url(bytes: ArrayBuffer | Uint8Array): string {
  let s = '';
  new Uint8Array(bytes).forEach((b) => (s += String.fromCharCode(b)));
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function decodeClaims(jwt: string): any {
  const part = jwt.split('.')[1] || '';
  const json = decodeURIComponent(
    atob(part.replace(/-/g, '+').replace(/_/g, '/'))
      .split('')
      .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
      .join(''),
  );
  return JSON.parse(json);
}

/**
 * Where to land after sign-in: a path on this site only. Anything else (another
 * site such as //evil.example, or the callback page itself) becomes '/'.
 */
function safeReturnTo(to: unknown): string {
  if (typeof to !== 'string' || !to.startsWith('/') || to.startsWith('//') || to.startsWith('/\\')) return '/';
  return to.startsWith(CALLBACK_PATH) ? '/' : to; // also /auth/callback.html
}

function setToken(next: string | null, persist = true) {
  clearTimeout(expiryTimer);
  token = next;
  user = null;
  if (next) {
    const c = safe(() => decodeClaims(next), null);
    if (c && c.exp * 1000 > Date.now()) {
      user = { id: c.sub, employeeId: c.employeeId, name: c.name, email: c.email ?? null, role: c.role, tags: c.tags || [], exp: c.exp };
      expiryTimer = setTimeout(() => {
        setToken(null);
        (need().onUnauthorized || (() => signIn()))();
      }, Math.min(c.exp * 1000 - Date.now(), 2 ** 31 - 1));
    } else {
      token = null;
    }
  }
  if (persist) safe(() => (token ? localStorage.setItem(storeKey(), token) : localStorage.removeItem(storeKey())), undefined);
  listeners.forEach((fn) => fn(user));
}

/** Call once at startup, before anything else. Restores a saved session. */
export function configure(o: FlamecoAuthOptions): void {
  opts = {
    ...o,
    authUrl: o.authUrl.replace(/\/+$/, ''),
    // Worked out here, in the browser, so server-rendered code never touches window.
    redirectUri: o.redirectUri ?? window.location.origin + '/auth/callback',
  };
  setToken(safe(() => localStorage.getItem(storeKey()), null));
  // Other tabs: follow their sign-in and sign-out instead of keeping an old token.
  window.addEventListener('storage', (e) => {
    if (e.key === storeKey() && e.newValue !== token) setToken(e.newValue, false);
  });
  if (o.watchSession !== false && sameSiteAsAuth()) {
    const check = () => void checkSession();
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', () => document.visibilityState === 'visible' && check());
    document.addEventListener('pointerdown', check, true);
    check();
  }
}

/** True when the browser sends the auth site's cookie with our requests (same site). */
function sameSiteAsAuth(): boolean {
  const a = safe(() => new URL(need().authUrl).hostname, '');
  const b = window.location.hostname;
  if (!a || !b) return false;
  if (a === b) return true;
  const isIp = (h: string) => /^[\d.]+$/.test(h) || h.includes(':');
  if (isIp(a) || isIp(b)) return false;
  const site = (h: string) => h.split('.').slice(-2).join('.');
  return site(a) === site(b);
}

/** Follows the shared Flameco session: signed out elsewhere, another person, renewal. */
async function checkSession(): Promise<void> {
  const c = need();
  if (!user || leaving || Date.now() - lastCheck < CHECK_EVERY_MS) return;
  lastCheck = Date.now();
  let s: { active?: boolean; userId?: string } | null = null;
  try {
    const res = await fetch(c.authUrl + '/v1/session/status', { credentials: 'include', cache: 'no-store' });
    if (!res.ok) return; // auth service trouble: keep working on the token we have
    s = await res.json();
  } catch {
    return; // offline: same
  }
  if (!user || !s) return;
  safe(() => sessionStorage.removeItem(silentKey()), undefined);
  if (!s.active || s.userId !== user.id) {
    setToken(null);
    (c.onUnauthorized || (() => signIn()))();
  } else if (user.exp * 1000 - Date.now() < RENEW_BEFORE_MS) {
    await signIn({ prompt: 'none' });
  }
}

/** The signed-in person, or null. */
export const getUser = (): FlamecoUser | null => user;
/** The access token for API calls, or null. */
export const getToken = (): string | null => token;
export const isSignedIn = (): boolean => !!user;
/** True when the signed-in person has this tag in this app. */
export const can = (tag: string): boolean => !!user && user.tags.includes(tag);

/** Subscribe to sign-in / sign-out. Returns an unsubscribe function. */
export function onChange(fn: (u: FlamecoUser | null) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Sends the person to the Flameco login page. `returnTo` is where to land
 * after sign-in (default: the current page). `prompt: 'none'` checks silently
 * and comes back with LoginRequiredError if nobody is signed in. With neither,
 * the first call in a tab tries silently first; `interactive: true` skips that.
 */
export async function signIn(o: { returnTo?: string; prompt?: 'none'; interactive?: boolean } = {}): Promise<void> {
  const c = need();
  if (leaving) return;
  let prompt = o.prompt;
  // interactive: a "Sign in" button the person pressed; go straight to the login page.
  if (!prompt && !o.interactive && c.silentSignIn !== false && !safe(() => sessionStorage.getItem(silentKey()), null)) {
    safe(() => sessionStorage.setItem(silentKey(), '1'), undefined);
    prompt = 'none';
  }
  if (prompt === 'none' && !c.navigate && sameSiteAsAuth()) {
    const got = await silentToken();
    if (got === 'signed-in') return;
    // Nobody signed in: straight to the login page. Network trouble: the usual way.
    if (got === 'nobody') prompt = undefined;
  }
  const verifier = b64url(crypto.getRandomValues(new Uint8Array(48)));
  const state = b64url(crypto.getRandomValues(new Uint8Array(16)));
  const challenge = b64url(await sha256(new TextEncoder().encode(verifier)));
  const returnTo = safeReturnTo(o.returnTo ?? window.location.pathname + window.location.search + window.location.hash);
  safe(() => sessionStorage.setItem(flowKey(), JSON.stringify({ verifier, state, returnTo })), undefined);
  // Capacitor: sessionStorage survives the in-app browser round trip; keep a copy in localStorage too.
  safe(() => localStorage.setItem(flowKey(), JSON.stringify({ verifier, state, returnTo })), undefined);

  const url = new URL(c.authUrl + '/login/'); // with the slash: /login answers with a redirect
  url.searchParams.set('client_id', c.clientId);
  url.searchParams.set('redirect_uri', c.redirectUri);
  url.searchParams.set('state', state);
  url.searchParams.set('code_challenge', challenge);
  url.searchParams.set('code_challenge_method', 'S256');
  if (prompt === 'none') url.searchParams.set('prompt', 'none');
  // replace, not assign: Back from the login page must not land on a page that
  // sends the person straight back to it.
  await (c.navigate ? c.navigate(url.toString()) : window.location.replace(url.toString()));
}

/** The shared session swapped for this app's token in one call, without leaving the page. */
async function silentToken(): Promise<'signed-in' | 'nobody' | 'failed'> {
  const c = need();
  try {
    const res = await fetch(c.authUrl + '/v1/session/token', {
      method: 'POST',
      credentials: 'include',
      cache: 'no-store',
      // A form body keeps this a "simple" request: no preflight round trip.
      body: new URLSearchParams({ clientId: c.clientId, redirectUri: c.redirectUri }),
    });
    if (res.status === 401) return 'nobody';
    const body = res.ok ? await res.json() : null;
    if (!body?.access_token) return 'failed';
    setToken(body.access_token);
    lastCheck = Date.now();
    safe(() => sessionStorage.removeItem(silentKey()), undefined);
    return user ? 'signed-in' : 'failed';
  } catch {
    return 'failed';
  }
}

/**
 * Finishes sign-in on the callback page. Returns where to go next.
 * Throws LoginRequiredError after a prompt=none check with nobody signed in.
 */
export async function handleCallback(href: string = window.location.href): Promise<{ returnTo: string }> {
  const c = need();
  const url = new URL(href);
  const raw = safe(() => sessionStorage.getItem(flowKey()) || localStorage.getItem(flowKey()), null);
  const flow = raw ? safe(() => JSON.parse(raw), null) : null;
  safe(() => sessionStorage.removeItem(flowKey()), undefined);
  safe(() => localStorage.removeItem(flowKey()), undefined);

  const error = url.searchParams.get('error');
  if (error === 'login_required') throw new LoginRequiredError('Not signed in', safeReturnTo(flow?.returnTo));
  if (error) throw new Error('Sign-in did not finish.');
  const code = url.searchParams.get('code');
  if (!code) return { returnTo: '/' }; // e.g. back from "sign out everywhere"
  if (!flow || url.searchParams.get('state') !== flow.state) {
    // An old callback link (reload, history) while already signed in: just go on.
    if (user) return { returnTo: '/' };
    throw new Error('This sign-in did not start in this browser.');
  }
  const res = await fetch(c.authUrl + '/v1/oauth/token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      grant_type: 'authorization_code',
      code,
      client_id: c.clientId,
      redirect_uri: c.redirectUri,
      code_verifier: flow.verifier,
    }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.message || 'Sign-in failed.');
  setToken(body.access_token);
  lastCheck = Date.now();
  safe(() => sessionStorage.removeItem(silentKey()), undefined);
  return { returnTo: safeReturnTo(flow.returnTo) };
}

/** fetch() that sends the Flameco token. On 401 the session is cleared. */
export async function authFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  const sent = token;
  if (sent) headers.set('Authorization', `Bearer ${sent}`);
  const res = await fetch(input, { ...init, headers });
  // Only the token this request carried was turned down. A call sent before
  // sign-in finished (no token, or an older one) must not throw away the new one.
  if (res.status === 401 && sent && sent === token) {
    // A token minutes old turned down: the API doesn't accept this sign-in (a
    // setup problem, not an expired session). Signing in silently would hand
    // over another token it turns down, over and over; let the login page
    // show instead, so it stops at one click.
    const iat = safe(() => decodeClaims(token as string).iat as number, 0);
    if (Date.now() / 1000 - iat < 120) safe(() => sessionStorage.setItem(silentKey(), '1'), undefined);
    setToken(null);
    (need().onUnauthorized || (() => signIn()))();
  }
  return res;
}

/**
 * Downloads a file from your API with the token (exports, CSV, PDF). A plain
 * link or window.open() cannot send it.
 */
export async function authDownload(input: RequestInfo | URL, filename: string, init: RequestInit = {}): Promise<void> {
  const res = await authFetch(input, init);
  if (!res.ok) throw new Error(res.status === 403 ? "You don't have access to this." : `Download failed (${res.status}).`);
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Signs out of this app. `everywhere: true` also ends the Flameco session for
 * all apps on this device, so the login page can't offer "Continue as" the
 * person who just left (use it on shared devices).
 */
export async function signOut(o: { everywhere?: boolean } = {}): Promise<void> {
  const c = need();
  const old = token;
  if (o.everywhere) leaving = true;
  // Forget the token first: a slow or offline network must not keep the session.
  setToken(null);
  if (old) {
    fetch(c.authUrl + '/v1/auth/logout', { method: 'POST', headers: { Authorization: `Bearer ${old}` }, keepalive: true }).catch(() => {});
  }
  if (o.everywhere) {
    const url = new URL(c.authUrl + '/logout');
    url.searchParams.set('client_id', c.clientId);
    url.searchParams.set('redirect_uri', c.redirectUri);
    await (c.navigate ? c.navigate(url.toString()) : window.location.assign(url.toString()));
  }
}
